import React, { useState, useEffect } from 'react';
import { AuthContext, API, BACKEND_URL } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FaWhatsapp, FaDumbbell, FaUtensils, FaChartLine, FaSignOutAlt, FaPlus, FaTrash } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ClientDashboard = () => {
  const { user, logout } = React.useContext(AuthContext);
  const token = localStorage.getItem('token');
  const [workouts, setWorkouts] = useState([]);
  const [exercises, setExercises] = useState({});
  const [nutritionGoals, setNutritionGoals] = useState([]);
  const [nutritionLog, setNutritionLog] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  const [selectedDay, setSelectedDay] = useState(1);
  const [showAddMeasurement, setShowAddMeasurement] = useState(false);
  const [meals, setMeals] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);

  const isPaid = user.is_paid && user.payment_expiry && new Date(user.payment_expiry) > new Date();

  useEffect(() => {
    if (isPaid) {
      fetchData();
    }
  }, [isPaid]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch workouts
      const workoutsRes = await axios.get(`${API}/workouts/client/${user.id}`, { headers });
      setWorkouts(workoutsRes.data);
      
      // Fetch exercises
      const exercisesRes = await axios.get(`${API}/exercises`, { headers });
      const exMap = {};
      exercisesRes.data.forEach(ex => {
        exMap[ex.id] = ex;
      });
      setExercises(exMap);
      
      // Fetch nutrition goals
      const goalsRes = await axios.get(`${API}/nutrition/goals/${user.id}`, { headers });
      setNutritionGoals(goalsRes.data);
      
      // Fetch today's nutrition log
      const today = new Date().toISOString().split('T')[0];
      const logRes = await axios.get(`${API}/nutrition/log/${today}`, { headers });
      if (logRes.data) {
        setNutritionLog(logRes.data);
        setMeals(logRes.data.meals);
      } else {
        setMeals([]);
      }
      
      // Fetch measurements
      const measurementsRes = await axios.get(`${API}/measurements/${user.id}`, { headers });
      setMeasurements(measurementsRes.data);
      
      // Fetch meal plans
      const mealPlansRes = await axios.get(`${API}/meal-plans/${user.id}`, { headers });
      setMealPlans(mealPlansRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleAddMeasurement = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const data = {
      week_date: formData.get('week_date'),
      weight: parseFloat(formData.get('weight')),
      waist: parseFloat(formData.get('waist')) || null,
      thighs: parseFloat(formData.get('thighs')) || null,
      chest: parseFloat(formData.get('chest')) || null,
      arms: parseFloat(formData.get('arms')) || null,
      hips: parseFloat(formData.get('hips')) || null
    };
    
    try {
      await axios.post(`${API}/measurements`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Merenje dodato!');
      setShowAddMeasurement(false);
      fetchData();
      e.target.reset();
    } catch (error) {
      toast.error('Greška pri dodavanju merenja');
    }
  };

  const getTodayGoal = () => {
    const dayOfWeek = new Date().getDay();
    const dayNum = dayOfWeek === 0 ? 5 : dayOfWeek > 5 ? 5 : dayOfWeek;
    return nutritionGoals.find(g => g.day === dayNum);
  };

  const calculateTotals = () => {
    return meals.reduce((acc, meal) => ({
      calories: acc.calories + meal.calories,
      protein: acc.protein + meal.protein,
      fats: acc.fats + meal.fats,
      carbs: acc.carbs + meal.carbs
    }), { calories: 0, protein: 0, fats: 0, carbs: 0 });
  };

  if (!isPaid) {
    const [paymentInfo, setPaymentInfo] = useState(null);

    useEffect(() => {
      const fetchPaymentInfo = async () => {
        try {
          const response = await axios.get(`${API}/payment-info`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setPaymentInfo(response.data);
        } catch (error) {
          console.error('Error fetching payment info:', error);
        }
      };
      fetchPaymentInfo();
    }, []);

    if (!paymentInfo) {
      return <div className="min-h-screen bg-black flex items-center justify-center">
        <p className="text-white">Učitavanje...</p>
      </div>;
    }

    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-3 sm:px-4">
        <div className="glass-dark rounded-3xl p-6 sm:p-12 max-w-2xl w-full">
          <h1 className="text-3xl sm:text-4xl font-black mb-6 sm:mb-8 text-white text-center">Podaci za uplatu</h1>
          <p className="text-base sm:text-xl mb-6 sm:mb-8 text-gray-300 text-center">
            Izvršite uplatu kako biste pristupili svom treningu{paymentInfo.package.includes('Ishrana') ? ' i planu ishrane' : ''}.
          </p>
          
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-[#36e199] rounded-2xl p-6 sm:p-8 mb-6">
            <div className="space-y-4">
              {/* First Payment Special Banner */}
              {paymentInfo.is_first_payment && (
                <div className="bg-green-900/30 border-2 border-green-500 rounded-xl p-4 mb-4">
                  <p className="text-green-400 text-lg font-bold text-center mb-1">SPECIJALNA PONUDA</p>
                  <p className="text-white text-center text-sm">PRVI MESEC - €30.00 za sve!</p>
                </div>
              )}
              
              <div className="text-center pb-4 border-b border-gray-700">
                <p className="text-gray-300 text-sm sm:text-base mb-2">Ukupan iznos za uplatu</p>
                <p className="text-4xl sm:text-5xl font-black accent-text">{paymentInfo.amount}</p>
                <p className="text-gray-400 text-sm mt-2">{paymentInfo.package}</p>
                {paymentInfo.is_first_payment && (
                  <p className="text-green-400 text-xs mt-2 font-semibold">Prvi mesec - specijalna cena!</p>
                )}
              </div>
              
              <div className="text-left space-y-3">
                <div className="bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-400 text-xs uppercase mb-1">Primalac</p>
                  <p className="text-white text-lg font-bold">{paymentInfo.recipient}</p>
                  <p className="text-gray-300 text-sm">{paymentInfo.recipient_location}</p>
                </div>
                
                <div className="bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-400 text-xs uppercase mb-1">Broj tekućeg računa primaoca</p>
                  <p className="text-white text-xl font-bold tracking-wider">{paymentInfo.account_number}</p>
                </div>
                
                <div className="bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-400 text-xs uppercase mb-1">Uplatilac</p>
                  <p className="text-white text-lg font-bold">{paymentInfo.payer}</p>
                </div>
                
                <div className="bg-gray-800 p-4 rounded-lg">
                  <p className="text-gray-400 text-xs uppercase mb-1">Svrha uplate</p>
                  <p className="text-white text-base">{paymentInfo.purpose}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <Button onClick={logout} className="bg-gray-700 hover:bg-gray-600 text-white font-bold w-full sm:w-auto" data-testid="logout-btn">
              Odjavi se
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const todayGoal = getTodayGoal();
  const totals = calculateTotals();
  const todayWorkout = workouts.find(w => w.day === selectedDay);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="client-dashboard">
      {/* Header */}
      <header className="glass-dark border-b border-gray-800 sticky top-0 z-50 animate-fade-in">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <img 
                src="/logo.png" 
                alt="ReStart Fit Logo" 
                className="h-10 sm:h-12 animate-scale-in"
              />
              <div className="hidden sm:block">
                <h1 className="text-lg sm:text-xl font-bold text-white">Dobrodošao, {user.first_name}!</h1>
                <p className="text-xs sm:text-sm text-gray-400">Aktivno do: {new Date(user.payment_expiry).toLocaleDateString('sr-RS')}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => window.open('https://wa.me/381691553565', '_blank')}
                className="bg-green-500 hover:bg-green-600 text-white text-xs sm:text-base hover:scale-105 transition-transform"
                size="sm"
                data-testid="whatsapp-btn"
              >
                <FaWhatsapp className="sm:mr-2" /> <span className="hidden sm:inline">Kontakt</span>
              </Button>
              <Button 
                onClick={logout} 
                variant="outline" 
                className="border-gray-700 text-white text-xs sm:text-base hover:scale-105 transition-transform" 
                size="sm"
                data-testid="logout-btn"
              >
                <FaSignOutAlt className="sm:mr-2" /> <span className="hidden sm:inline">Odjava</span>
              </Button>
            </div>
          </div>
          <div className="sm:hidden mt-2">
            <p className="text-sm font-semibold text-white">Dobrodošao, {user.first_name}!</p>
            <p className="text-xs text-gray-400">Aktivno do: {new Date(user.payment_expiry).toLocaleDateString('sr-RS')}</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <Tabs defaultValue="treninzi" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-gray-900 mb-4 sm:mb-8 p-1 animate-slide-in-up">
            <TabsTrigger 
              value="treninzi" 
              data-testid="training-tab"
              className="text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 py-2 transition-all hover:scale-105"
            >
              <FaDumbbell className="text-sm sm:text-base" /> 
              <span className="hidden sm:inline">Treninzi</span>
              <span className="sm:hidden">Vežbe</span>
            </TabsTrigger>
            <TabsTrigger 
              value="ishrana" 
              data-testid="nutrition-tab"
              className="text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 py-2 transition-all hover:scale-105"
            >
              <FaUtensils className="text-sm sm:text-base" /> 
              <span>Ishrana</span>
            </TabsTrigger>
            <TabsTrigger 
              value="merenja" 
              data-testid="measurements-tab"
              className="text-xs sm:text-base flex items-center justify-center gap-1 sm:gap-2 py-2 transition-all hover:scale-105"
            >
              <FaChartLine className="text-sm sm:text-base" /> 
              <span>Merenja</span>
            </TabsTrigger>
          </TabsList>

          {/* Treninzi Tab */}
          <TabsContent value="treninzi">
            <div className="mb-4 sm:mb-6">
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                {[1, 2, 3, 4, 5].map(day => (
                  <Button
                    key={day}
                    onClick={() => setSelectedDay(day)}
                    size="sm"
                    className={`${selectedDay === day ? 'accent-bg text-black font-bold' : 'bg-gray-800 text-white'} flex-shrink-0 min-w-[70px] sm:min-w-[90px]`}
                    data-testid={`day-${day}-btn`}
                  >
                    Dan {day}
                  </Button>
                ))}
              </div>
            </div>

            {todayWorkout && todayWorkout.exercises.length > 0 ? (
              <div className="space-y-4">
                {todayWorkout.exercises.map((ex, idx) => {
                  const exercise = exercises[ex.exercise_id];
                  if (!exercise) return null;
                  
                  return (
                    <Card key={idx} className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 animate-slide-in-up hover:scale-[1.02] transition-transform duration-300" data-testid={`exercise-card-${idx}`} style={{animationDelay: `${idx * 0.1}s`}}>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-white text-lg sm:text-xl">{exercise.name}</CardTitle>
                        <CardDescription className="text-gray-300 text-sm sm:text-base">{exercise.muscle_group}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3 sm:space-y-4">
                        <p className="text-gray-200 text-sm sm:text-base">{exercise.description}</p>
                        
                        {/* Show reps or time based on exercise type */}
                        {ex.exercise_type === 'time' ? (
                          <div>
                            <p className="font-semibold mb-2 text-white text-sm sm:text-base">Vreme trajanja:</p>
                            <span className="accent-bg text-black px-4 sm:px-5 py-2 rounded-full font-bold text-base sm:text-lg inline-block hover:scale-110 transition-transform">
                              {ex.time_duration}
                            </span>
                          </div>
                        ) : (
                          <div>
                            <p className="font-semibold mb-2 text-white text-sm sm:text-base">Serije i ponavljanja:</p>
                            <div className="flex gap-2 flex-wrap">
                              {ex.sets && ex.sets.map((reps, setIdx) => (
                                <span key={setIdx} className="accent-bg text-black px-2 sm:px-3 py-1 rounded-full font-bold text-xs sm:text-sm hover:scale-110 transition-transform">
                                  Set {setIdx + 1}: {reps} ponavljanja
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {exercise.youtube_url && (
                          <div className="relative rounded-lg overflow-hidden hover:shadow-lg hover:shadow-[#36e19933] transition-shadow" style={{paddingBottom: '56.25%'}}>
                            <iframe
                              className="absolute top-0 left-0 w-full h-full"
                              src={`https://www.youtube.com/embed/${exercise.youtube_url.includes('youtu.be') ? exercise.youtube_url.split('youtu.be/')[1]?.split('?')[0] : exercise.youtube_url.includes('watch?v=') ? exercise.youtube_url.split('watch?v=')[1]?.split('&')[0] : exercise.youtube_url}`}
                              title={exercise.name}
                              frameBorder="0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              data-testid={`exercise-video-${idx}`}
                            ></iframe>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                  })}
                </div>
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <p className="text-gray-300 text-lg">Nema dodeljenih vežbi za ovaj dan</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Ishrana Tab */}
          <TabsContent value="ishrana">
            {/* Meal Plans by Day */}
            {mealPlans.length > 0 && (
              <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 mb-6 animate-slide-in-up">
                <CardHeader>
                  <CardTitle className="text-white text-xl">Plan ishrane po danima</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-7 gap-2 mb-4">
                    {[1, 2, 3, 4, 5, 6, 7].map(day => {
                      const dayPlan = mealPlans.find(p => p.day === day);
                      const hasPlan = dayPlan && dayPlan.meals.length > 0;
                      return (
                        <button
                          key={day}
                          onClick={() => setSelectedDay(day)}
                          className={`p-2 rounded-lg text-center transition-all text-xs ${
                            selectedDay === day 
                              ? 'accent-bg text-black font-bold' 
                              : hasPlan 
                                ? 'bg-blue-600 text-white hover:bg-blue-700'
                                : 'bg-gray-700 text-gray-400'
                          }`}
                        >
                          <div className="text-xs mb-1">Dan</div>
                          <div className="text-lg font-bold">{day}</div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {(() => {
                    const dayPlan = mealPlans.find(p => p.day === selectedDay);
                    if (dayPlan && dayPlan.meals.length > 0) {
                      const mealTypeLabels = {
                        'dorucak': 'Doručak',
                        'rucak': 'Ručak',
                        'vecera': 'Večera',
                        'uzina': 'Užina'
                      };
                      return (
                        <div className="space-y-3">
                          <h4 className="text-white font-medium text-lg">Dan {selectedDay}:</h4>
                          {dayPlan.meals.map((meal, idx) => (
                            <div key={idx} className="bg-gray-800 p-4 rounded-lg border border-gray-700">
                              <p className="text-white font-semibold text-base mb-2">{mealTypeLabels[meal.meal_type] || meal.meal_type}</p>
                              <p className="text-gray-300 text-sm">{meal.description}</p>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return (
                      <p className="text-gray-400 text-center py-4">
                        Dan {selectedDay} nema plana ishrane
                      </p>
                    );
                  })()}
                </CardContent>
              </Card>
            )}

            {todayGoal && (
              <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 mb-6 animate-slide-in-up">
                <CardHeader>
                  <CardTitle className="text-white text-xl">Današnji ciljevi</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="animate-fade-in animate-delay-100">
                      <p className="text-gray-300 text-base font-medium">Kalorije</p>
                      <p className="text-2xl font-bold accent-text" data-testid="calories-goal">{todayGoal.calories} kcal</p>
                      <p className="text-sm text-gray-400">Preostalo: {Math.max(0, todayGoal.calories - totals.calories).toFixed(0)}</p>
                    </div>
                    <div className="animate-fade-in animate-delay-200">
                      <p className="text-gray-300 text-base font-medium">Proteini</p>
                      <p className="text-2xl font-bold accent-text" data-testid="protein-goal">{todayGoal.protein}g</p>
                      <p className="text-sm text-gray-400">Preostalo: {Math.max(0, todayGoal.protein - totals.protein).toFixed(0)}g</p>
                    </div>
                    <div className="animate-fade-in animate-delay-300">
                      <p className="text-gray-300 text-base font-medium">Masti</p>
                      <p className="text-2xl font-bold accent-text" data-testid="fats-goal">{todayGoal.fats}g</p>
                      <p className="text-sm text-gray-400">Preostalo: {Math.max(0, todayGoal.fats - totals.fats).toFixed(0)}g</p>
                    </div>
                    <div className="animate-fade-in animate-delay-400">
                      <p className="text-gray-300 text-base font-medium">Ugljeni hidrati</p>
                      <p className="text-2xl font-bold accent-text" data-testid="carbs-goal">{todayGoal.carbs}g</p>
                      <p className="text-sm text-gray-400">Preostalo: {Math.max(0, todayGoal.carbs - totals.carbs).toFixed(0)}g</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Merenja Tab */}
          <TabsContent value="merenja">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
              <h2 className="text-xl sm:text-2xl font-bold text-white">Nedeljni progres</h2>
              <Button 
                onClick={() => setShowAddMeasurement(true)} 
                className="accent-bg text-black font-bold w-full sm:w-auto text-sm sm:text-base" 
                size="sm"
                data-testid="add-measurement-btn"
              >
                <FaPlus className="mr-1 sm:mr-2" /> Dodaj merenje
              </Button>
            </div>

            {measurements.length > 0 ? (
              <>
                {/* Weekly Progress Report */}
                {measurements.length >= 2 && (() => {
                  const latest = measurements[measurements.length - 1];
                  const previous = measurements[measurements.length - 2];
                  const weightDiff = latest.weight - previous.weight;
                  const weeksDiff = Math.abs(Math.round((new Date(latest.week_date) - new Date(previous.week_date)) / (7 * 24 * 60 * 60 * 1000)));
                  
                  let waistDiff = null;
                  if (latest.waist && previous.waist) {
                    waistDiff = latest.waist - previous.waist;
                  }
                  
                  return (
                    <Card className="bg-gradient-to-r from-green-900 to-green-800 border-green-700 mb-6">
                      <CardHeader>
                        <CardTitle className="text-white text-xl flex items-center gap-2">
                          <FaChartLine /> Tvoj napredak kroz {weeksDiff > 1 ? `${weeksDiff} nedelje` : 'nedelju'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-white">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between bg-black/20 p-3 rounded-lg">
                            <span className="font-medium">Promena težine:</span>
                            <span className={`text-2xl font-bold ${weightDiff < 0 ? 'text-green-400' : weightDiff > 0 ? 'text-red-400' : 'text-gray-300'}`}>
                              {weightDiff > 0 ? '+' : ''}{weightDiff.toFixed(1)} kg
                              {weightDiff < 0 && ' 🎉'}
                            </span>
                          </div>
                          
                          {waistDiff !== null && (
                            <div className="flex items-center justify-between bg-black/20 p-3 rounded-lg">
                              <span className="font-medium">Promena struka:</span>
                              <span className={`text-2xl font-bold ${waistDiff < 0 ? 'text-green-400' : waistDiff > 0 ? 'text-red-400' : 'text-gray-300'}`}>
                                {waistDiff > 0 ? '+' : ''}{waistDiff.toFixed(1)} cm
                                {waistDiff < 0 && ' 🎉'}
                              </span>
                            </div>
                          )}
                          
                          <div className="mt-4 pt-3 border-t border-white/20">
                            <p className="text-sm text-gray-200">
                              {weightDiff < -1 ? '🔥 Odličan napredak! Nastavi ovim tempom!' : 
                               weightDiff < 0 ? '💪 Dobar posao! Vidljiv je napredak!' :
                               weightDiff === 0 ? '✅ Održavaš težinu, nastavi tako!' :
                               '📊 Fokusiraj se na ishranu i trening prema planu!'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Progress Charts */}
                <div className="space-y-6 mb-8">
                  <h3 className="text-xl font-bold text-white">Grafički prikaz progresa</h3>
                  
                  {/* Weight Chart */}
                  <Card className="bg-gray-800 border-gray-700">
                    <CardHeader>
                      <CardTitle className="text-white">Težina kroz vreme</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={measurements.map(m => ({
                          date: new Date(m.week_date).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit' }),
                          weight: m.weight
                        }))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis dataKey="date" stroke="#9CA3AF" />
                          <YAxis stroke="#9CA3AF" />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                            labelStyle={{ color: '#F3F4F6' }}
                            itemStyle={{ color: '#36e199' }}
                          />
                          <Legend wrapperStyle={{ color: '#F3F4F6' }} />
                          <Line type="monotone" dataKey="weight" stroke="#36e199" strokeWidth={3} name="Težina (kg)" />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Body Measurements Chart */}
                  {measurements.some(m => m.waist || m.chest || m.thighs || m.arms || m.hips) && (
                    <Card className="bg-gray-800 border-gray-700">
                      <CardHeader>
                        <CardTitle className="text-white">Telesna merenja kroz vreme</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={measurements.map(m => ({
                            date: new Date(m.week_date).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit' }),
                            waist: m.waist,
                            chest: m.chest,
                            thighs: m.thighs,
                            arms: m.arms,
                            hips: m.hips
                          }))}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                            <XAxis dataKey="date" stroke="#9CA3AF" />
                            <YAxis stroke="#9CA3AF" />
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151' }}
                              labelStyle={{ color: '#F3F4F6' }}
                              itemStyle={{ color: '#36e199' }}
                            />
                            <Legend wrapperStyle={{ color: '#F3F4F6' }} />
                            {measurements.some(m => m.waist) && <Line type="monotone" dataKey="waist" stroke="#36e199" strokeWidth={2} name="Struk (cm)" />}
                            {measurements.some(m => m.chest) && <Line type="monotone" dataKey="chest" stroke="#3b82f6" strokeWidth={2} name="Grudi (cm)" />}
                            {measurements.some(m => m.thighs) && <Line type="monotone" dataKey="thighs" stroke="#f59e0b" strokeWidth={2} name="Butine (cm)" />}
                            {measurements.some(m => m.arms) && <Line type="monotone" dataKey="arms" stroke="#ef4444" strokeWidth={2} name="Ruke (cm)" />}
                            {measurements.some(m => m.hips) && <Line type="monotone" dataKey="hips" stroke="#8b5cf6" strokeWidth={2} name="Bokovi (cm)" />}
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  )}
                </div>

                <h3 className="text-xl font-bold text-white mb-4">Istorija merenja</h3>
                <div className="space-y-4">
                  {measurements.map((m, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700" data-testid={`measurement-card-${idx}`}>
                    <CardHeader>
                      <CardTitle className="text-white text-xl">Nedelja: {new Date(m.week_date).toLocaleDateString('sr-RS')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-gray-300 text-base font-medium">Težina</p>
                          <p className="text-xl font-bold text-white">{m.weight} kg</p>
                        </div>
                        {m.waist && (
                          <div>
                            <p className="text-gray-300 text-base font-medium">Struk</p>
                            <p className="text-xl font-bold text-white">{m.waist} cm</p>
                          </div>
                        )}
                        {m.thighs && (
                          <div>
                            <p className="text-gray-300 text-base font-medium">Butine</p>
                            <p className="text-xl font-bold text-white">{m.thighs} cm</p>
                          </div>
                        )}
                        {m.chest && (
                          <div>
                            <p className="text-gray-300 text-base font-medium">Grudi</p>
                            <p className="text-xl font-bold text-white">{m.chest} cm</p>
                          </div>
                        )}
                        {m.arms && (
                          <div>
                            <p className="text-gray-300 text-base font-medium">Ruke</p>
                            <p className="text-xl font-bold text-white">{m.arms} cm</p>
                          </div>
                        )}
                        {m.hips && (
                          <div>
                            <p className="text-gray-300 text-base font-medium">Bokovi</p>
                            <p className="text-xl font-bold text-white">{m.hips} cm</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              </>
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <p className="text-gray-300 text-lg">Nema unetih merenja</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Measurement Dialog */}
      <Dialog open={showAddMeasurement} onOpenChange={setShowAddMeasurement}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white" data-testid="add-measurement-dialog">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">Dodaj nedeljno merenje</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddMeasurement} className="space-y-4">
            <div>
              <Label htmlFor="week_date" className="text-gray-200">Datum nedelje</Label>
              <Input id="week_date" name="week_date" type="date" required className="bg-gray-800 border-gray-600 text-white" data-testid="measurement-date-input" />
            </div>
            <div>
              <Label htmlFor="weight" className="text-gray-200">Težina (kg)</Label>
              <Input id="weight" name="weight" type="number" step="0.1" required className="bg-gray-800 border-gray-600 text-white" data-testid="measurement-weight-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="waist" className="text-gray-200">Struk (cm)</Label>
                <Input id="waist" name="waist" type="number" step="0.1" className="bg-gray-800 border-gray-600 text-white" data-testid="measurement-waist-input" />
              </div>
              <div>
                <Label htmlFor="thighs" className="text-gray-200">Butine (cm)</Label>
                <Input id="thighs" name="thighs" type="number" step="0.1" className="bg-gray-800 border-gray-600 text-white" data-testid="measurement-thighs-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="chest" className="text-gray-200">Grudi (cm)</Label>
                <Input id="chest" name="chest" type="number" step="0.1" className="bg-gray-800 border-gray-600 text-white" data-testid="measurement-chest-input" />
              </div>
              <div>
                <Label htmlFor="arms" className="text-gray-200">Ruke (cm)</Label>
                <Input id="arms" name="arms" type="number" step="0.1" className="bg-gray-800 border-gray-600 text-white" data-testid="measurement-arms-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="hips" className="text-gray-200">Bokovi (cm)</Label>
              <Input id="hips" name="hips" type="number" step="0.1" className="bg-gray-800 border-gray-600 text-white" data-testid="measurement-hips-input" />
            </div>
            <Button type="submit" className="w-full accent-bg text-black font-bold" data-testid="submit-measurement-btn">Dodaj</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDashboard;
